import { Upload01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { usePhoneJsonEditor } from "./editor-context";

export type MediaUrlFieldProps = {
  id?: string;
  label: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  /** Hint for server gen_meta / accept filter. Default image. */
  mediaKind?: "image" | "audio" | "video" | "pdf";
  /** Optional field key written to upload metadata. */
  fieldKey?: string;
  onChange: (next: string) => void;
  /**
   * Takes over from onChange after a successful upload, so a caller can write
   * the URL plus the file's own name and size in one edit.
   */
  onUpload?: (uploaded: { url: string; name: string; size: number }) => void;
};

function looksLikeImageUrl(url: string): boolean {
  if (!url) return false;
  if (url.includes("/api/media/assets/")) return true;
  return /\.(webp|png|jpe?g|gif|avif|bmp)(\?|#|$)/i.test(url);
}

export function MediaUrlField({
  id,
  label,
  value,
  disabled,
  placeholder = "https://…",
  mediaKind = "image",
  fieldKey,
  onChange,
  onUpload,
}: MediaUrlFieldProps) {
  const editor = usePhoneJsonEditor();
  const inputId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controlId = id ?? inputId;
  const accept =
    mediaKind === "audio"
      ? "audio/*"
      : mediaKind === "video"
        ? "video/*"
        : mediaKind === "pdf"
          ? "application/pdf,.pdf"
          : "image/*";
  const canUpload = Boolean(editor?.versionId) && !disabled;

  const onPick = async (file: File | null) => {
    if (!file || !editor?.versionId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.uploadAsset(editor.versionId, file, {
        fieldKey: fieldKey ?? label,
      });
      const url = result.asset?.publicUrl;
      if (!url) {
        throw new ApiError(500, "upload_no_url");
      }
      if (onUpload) {
        onUpload({ url, name: file.name, size: file.size });
      } else {
        onChange(url);
      }
      toast.add({
        type: "success",
        title: "上传成功",
        description: file.name,
      });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "upload_failed";
      const note =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setError(note);
      toast.add({
        type: "error",
        title: "上传失败",
        description: note,
        priority: "high",
      });
    } finally {
      setBusy(false);
    }
  };

  const trimmed = value.trim();
  const showImagePreview =
    mediaKind === "image" && trimmed !== "" && looksLikeImageUrl(value);
  const showAudioPreview = mediaKind === "audio" && trimmed !== "";
  const showPdfLink =
    mediaKind === "pdf" && /^(https:\/\/|\/)/i.test(trimmed);
  const uploadLabel = trimmed ? "替换" : "上传";

  return (
    <Field>
      <FieldLabel htmlFor={controlId}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <Input
          id={controlId}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          className="min-w-0 flex-1"
          onChange={(e) => {
            setError(null);
            onChange(e.target.value);
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          className="sr-only"
          disabled={!canUpload || busy}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            void onPick(f);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!canUpload || busy}
          className="shrink-0"
          title={canUpload ? `${uploadLabel}文件并填入 URL` : "无法上传"}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? (
            <Spinner />
          ) : (
            <HugeiconsIcon icon={Upload01Icon} data-icon="inline-start" />
          )}
          {uploadLabel}
        </Button>
      </div>
      {showImagePreview ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-block"
        >
          <img
            src={value}
            alt=""
            className="h-14 max-w-full rounded-md border border-border object-cover"
          />
        </a>
      ) : null}
      {showAudioPreview ? (
        <audio
          src={value}
          controls
          preload="metadata"
          className="mt-1 w-full"
        />
      ) : null}
      {showPdfLink ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="mt-1 text-xs text-primary underline-offset-2 hover:underline"
        >
          打开 PDF
        </a>
      ) : null}
      {error ? (
        <p className="text-[0.65rem] text-destructive">{error}</p>
      ) : null}
    </Field>
  );
}
